/* ==========================================================================
   Build Your Plate — number-plate builder
   Drives the live reg preview (front + rear), resolves the selected Shopify
   variant, keeps price + line-item properties in sync, and gates checkout on a
   registration being entered.

   Paid extras and the plate are submitted together, preserving document files.
   The border is a nested cart item attached to its plate, charged once for
   either a single plate or a full set.
   ========================================================================== */
if (!customElements.get('plate-builder')) {
  customElements.define(
    'plate-builder',
    class PlateBuilder extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-plate-builder-data]');
        if (!dataEl) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('[plate-builder] bad data JSON', e);
          return;
        }

        this.mergeRelatedProducts();

        // Normalise product option names to lowercase for matching.
        this.optionNames = (this.data.options || []).map((o) => String(o).toLowerCase());

        // Which variant option index (0-based) each control maps to, or -1 if
        // that choice isn't a real variant dimension (then it's a property).
        this.styleIdx = this.optionNames.indexOf(this.dataset.styleOption);
        this.sizeIdx = this.optionNames.indexOf(this.dataset.sizeOption);
        this.positionIdx = this.optionNames.indexOf(this.dataset.positionOption);
        this.vehicleIdx = this.optionNames.indexOf(this.dataset.vehicleOption);
        this.populateSizes();

        this.requireReg = this.dataset.requireReg !== 'false';
        this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]+>/g, '');
        this.deliveryCents = parseInt(this.dataset.deliveryPrice, 10) || 0;
        this.deliveryPriceFormatted = this.dataset.deliveryPriceFormatted || '';

        // Cache elements.
        this.regInput = this.querySelector('[data-reg-input]');
        this.regError = this.querySelector('[data-reg-error]');
        this.previewTexts = this.querySelectorAll('[data-plate-preview-text]');
        this.stack = this.querySelector('[data-preview-stack]');
        this.previewPlate = this.querySelector('[data-preview-plate]');
        this.previewFront = this.querySelector('[data-preview-front]');
        this.previewRear = this.querySelector('[data-preview-rear]');
        this.variantIdInput = this.querySelector('[data-variant-id]');
        this.totalEl = this.querySelector('[data-total]');
        this.platePriceEl = this.querySelector('[data-plate-price]');
        this.summaryEl = this.querySelector('[data-plate-summary]');
        this.deliveryLine = this.querySelector('[data-line-delivery]');
        this.deliveryPriceEl = this.querySelector('[data-delivery-price-text]');
        this.submitBtn = this.querySelector('[data-submit]');
        this.submitText = this.querySelector('[data-submit-text]');
        this.form = this.querySelector('form');
        this.productForm = this.querySelector('product-form');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.documentUploads = Array.from(this.querySelectorAll('[data-number-plate-document-upload]'));
        this.sectionRoot = this.closest('.build-your-plate') || this;
        this.positionField = this.querySelector('[data-group="position"]');
        this.differentSizesInput = this.querySelector('[data-different-sizes]');
        if (this.positionField) {
          const pair = Array.from(this.positionField.querySelectorAll('input')).find(input => this.isFullSet(input.value));
          if (pair) pair.closest('label').parentElement.prepend(pair.closest('label'));
        }
        this.styleField = this.querySelector('[data-group="style"]');
        this.bikeStyleField = this.querySelector('[data-group="bike-style"]');
        this.bikeVariantInputs = Array.from(this.querySelectorAll('[data-bike-variant]'));
        this.fulfilmentField = this.querySelector('[data-group="fulfilment"]');
        this.fulfilmentInputs = Array.from(this.querySelectorAll('[data-fulfilment]'));
        this.legalStyleCards = Array.from(this.sectionRoot.querySelectorAll('[data-style-card-group="legal"]'));
        this.bikeStyleCards = Array.from(this.sectionRoot.querySelectorAll('[data-style-card-group="bike"]'));

        // Fitting kit add-on (optional — only present when configured).
        this.addonInput = this.querySelector('[data-addon]');
        this.badgeField = this.querySelector('[data-badge-field]');
        this.badgeInputs = Array.from(this.querySelectorAll('[data-badge]'));
        this.badgeVariants = JSON.parse(this.querySelector('[data-badge-data]')?.textContent || '[]');
        this.borderInput = this.querySelector('[data-border]');
        this.borderLine = this.querySelector('[data-line-border]');
        this.borderId = this.borderInput ? this.borderInput.dataset.borderId : null;
        this.borderCents = this.borderInput ? parseInt(this.borderInput.dataset.borderPrice, 10) || 0 : 0;
        this.addonField = this.querySelector('[data-group="addon"]');
        this.addonLine = this.querySelector('[data-line-addon]');
        this.addonSummary = this.querySelector('[data-addon-summary]');
        this.addonLineIsControl = !!(this.addonLine && this.addonInput && this.addonLine.contains(this.addonInput));
        // NB: the price span shares [data-addon-price] with the checkbox input,
        // so scope the lookup to the breakdown line to get the span, not the input.
        this.addonPriceEl = this.addonLine ? this.addonLine.querySelector('[data-addon-price]') : null;
        if (this.addonInput) {
          this.addonInitiallyDisabled = this.addonInput.disabled;
          this.addonId = this.addonInput.dataset.addonId || null;
          this.addonCents = parseInt(this.addonInput.dataset.addonPrice, 10) || 0;
          this.addonPriceFormatted = this.addonInput.dataset.addonPriceFormatted || '';
          this.addonTitle = this.addonInput.dataset.addonTitle || '';
        }

        this.keyringField = this.querySelector('[data-group="keyring"]');
        this.keyringInputs = this.querySelectorAll('[data-keyring]');
        this.keyringReview = this.querySelector('[data-keyring-review]');
        this.keyringReviewImage = this.querySelector('[data-keyring-review-image]');
        this.keyringReviewColour = this.querySelector('[data-keyring-review-colour]');

        this.propReg = this.querySelector('[data-prop-reg]');
        this.propKeyring = this.querySelector('[data-prop-keyring]');
        this.propFulfilment = this.querySelector('[data-prop-fulfilment]');
        this.propStyle = this.querySelector('[data-prop-style]');
        this.propPosition = this.querySelector('[data-prop-position]');
        this.propVehicle = this.querySelector('[data-prop-vehicle]');
        this.propFrontSize = this.querySelector('[data-prop-front-size]');
        this.propRearSize = this.querySelector('[data-prop-rear-size]');

        this.placeholder = (this.regInput && this.regInput.getAttribute('placeholder')) || '';
        this.defaultLabel = this.submitText ? this.submitText.textContent.trim() : 'Add to Basket';

        // Safety: if the configured default position matched nothing, make sure
        // one position is still selected so a variant can resolve.
        if (!this.querySelector('input[data-option="position"]:checked')) {
          const firstPos = this.querySelector('input[data-option="position"]');
          if (firstPos) firstPos.checked = true;
        }

        // Start on an available finish, including when Printed is not stocked yet.
        const initialStyles = Array.from(this.querySelectorAll('input[data-option="style"]'));
        const initialStyle = initialStyles.find((input) => {
          const variant = this.findVariant(input.value, this.selected('position'), this.selected('vehicle'));
          return variant && variant.available;
        });
        if (initialStyle) initialStyle.checked = true;

        this.setupWizard();
        this.bindEvents();
        this.setupMobileFieldVisibility();
        this.updatePreview();
        this.update();
        if (this.stack) {
          this.previewResizeObserver = new ResizeObserver(() => this.updatePreviewGeometry());
          this.previewResizeObserver.observe(this.stack);
        }
        document.fonts.ready.then(() => {
          if (this.isConnected) this.updatePreviewGeometry();
        });
      }

      disconnectedCallback() {
        if (this.previewResizeObserver) this.previewResizeObserver.disconnect();
        this.fieldVisibilityEvents?.abort();
        cancelAnimationFrame(this.fieldVisibilityFrame);
      }

      setupMobileFieldVisibility() {
        this.fieldVisibilityEvents?.abort();
        this.fieldVisibilityEvents = new AbortController();
        const options = { signal: this.fieldVisibilityEvents.signal };
        const schedule = () => {
          cancelAnimationFrame(this.fieldVisibilityFrame);
          this.fieldVisibilityFrame = requestAnimationFrame(() => this.keepMobileFieldVisible());
        };
        this.addEventListener('focusin', schedule, options);
        window.addEventListener('resize', schedule, options);
        // Mobile keyboards can resize/pan the visual viewport without resizing the page.
        window.visualViewport?.addEventListener('resize', schedule, options);
        window.visualViewport?.addEventListener('scroll', schedule, options);
        const revealBuilder = () => {
          if (window.location.hash === `#${this.sectionRoot.id}`) this.scrollToMobileStep();
        };
        window.addEventListener('hashchange', revealBuilder, options);
        window.addEventListener('load', revealBuilder, { ...options, once: true });
        if (document.readyState === 'complete') revealBuilder();
        document.addEventListener('click', event => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (!window.matchMedia('(max-width: 989px)').matches) return;
          const link = event.target.closest('a[href]');
          if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
          const url = new URL(link.href);
          if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || url.hash !== `#${this.sectionRoot.id}`) return;
          event.preventDefault();
          if (location.hash !== url.hash) history.pushState(null, '', url.hash);
          this.stepPanels?.[this.currentStep].querySelector('h3').focus({ preventScroll: true });
          this.scrollToMobileStep();
        }, options);
      }

      scrollToMobileStep() {
        if (!window.matchMedia('(max-width: 989px)').matches || !this.stepPanels) return;
        const navigation = this.querySelector('[data-step-navigation]');
        const headerHeight = document.querySelector('.shopify-section-header-sticky')?.getBoundingClientRect().height || 0;
        window.scrollTo({
          top: window.scrollY + navigation.getBoundingClientRect().top - headerHeight - 12,
          behavior: 'instant',
        });
      }

      keepMobileFieldVisible() {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        const field = document.activeElement;
        if (!this.contains(field) || !field.matches('input[type="text"], input[type="email"], input[type="tel"], input[type="number"], input[type="search"], input[type="url"], input:not([type]), textarea, select')) return;
        const bounds = field.getBoundingClientRect();
        if (!bounds.height) return;
        const viewport = window.visualViewport;
        const viewportTop = viewport?.offsetTop || 0;
        const header = document.querySelector('.shopify-section-header-sticky');
        const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
        const top = Math.max(viewportTop, headerBottom) + 16;
        const bottom = viewportTop + (viewport?.height || window.innerHeight) - 16;
        if (bounds.top >= top && bounds.bottom <= bottom) return;
        // Avoid an animated scroll competing with the keyboard's own viewport movement.
        const targetTop = top + Math.max(0, (bottom - top - bounds.height) / 2);
        window.scrollBy({ top: bounds.top - targetTop, behavior: 'instant' });
      }

      setupWizard() {
        const navigation = this.querySelector('[data-step-navigation]');
        if (!navigation || !this.form) return;
        this.stepPanels = [];
        this.stepButtons = Array.from(navigation.querySelectorAll('[data-step-go]'));
        this.currentStep = 0;
        this.highestStep = 0;
        const definitions = [
          ['Choose your plates', '[data-group="vehicle"], [data-group="size"], [data-group="position"], [data-group="bike-size"]'],
          ['Enter your registration', '[data-reg-fields]'],
          ['Choose your lettering', '[data-group="style"], [data-group="bike-style"]'],
          ['Add the finishing touches', '[data-group="extras"], [data-group="keyring"]'],
          ['Review your plates', '[data-group="fulfilment"], [data-number-plate-documents], [data-keyring-review], .plate-builder__summary, .plate-builder__turnaround, .product-form__buttons'],
        ];
        definitions.forEach(([title, selector], index) => {
          const panel = document.createElement('section');
          panel.className = 'plate-builder__step';
          panel.dataset.builderStep = String(index);
          const heading = document.createElement('h3');
          heading.id = `plate-step-${this.dataset.sectionId}-${index}`;
          heading.className = 'plate-builder__step-title';
          heading.textContent = title;
          heading.tabIndex = -1;
          panel.setAttribute('aria-labelledby', heading.id);
          panel.append(heading);
          this.querySelectorAll(selector).forEach(element => panel.append(element));
          if (index === 0 && this.positionField) {
            panel.querySelector('[data-group="size"]').before(this.positionField);
          }
          this.form.append(panel);
          this.stepPanels.push(panel);
        });
        const review = document.createElement('p');
        review.className = 'plate-builder__review-reg';
        this.reviewRegistration = review;
        this.stepPanels[4].querySelector('h3').after(review);
        const footer = document.createElement('div');
        footer.className = 'plate-builder__step-footer';
        this.backButton = document.createElement('button');
        this.backButton.type = 'button';
        this.backButton.className = 'plate-builder__back';
        this.backButton.textContent = 'Back';
        this.nextButton = document.createElement('button');
        this.nextButton.type = 'button';
        this.nextButton.className = 'button button--primary plate-builder__next';
        this.stepStatus = document.createElement('p');
        this.stepStatus.className = 'plate-builder__step-status';
        this.stepStatus.setAttribute('aria-live', 'polite');
        footer.append(this.backButton, this.nextButton);
        this.form.append(footer, this.stepStatus);
        this.backButton.addEventListener('click', () => {
          const steps = this.activeSteps();
          this.goToStep(steps[steps.indexOf(this.currentStep) - 1]);
        });
        this.nextButton.addEventListener('click', () => this.advanceStep());
        this.stepButtons.forEach(button => button.addEventListener('click', () => this.goToStep(Number(button.dataset.stepGo))));
        this.classList.add('is-wizard');
        this.querySelectorAll('.plate-builder__label').forEach(label => {
          if (label.firstChild && label.firstChild.nodeType === Node.TEXT_NODE) label.firstChild.textContent = label.firstChild.textContent.replace(/^\s*[1-4]\.\s*/, '');
        });
        this.querySelectorAll('[data-group="style"] > legend, [data-group="bike-style"] > legend, [data-group="extras"] > legend, [data-reg-fields] > label').forEach(label => label.classList.add('visually-hidden'));
        this.sectionRoot.classList.add('build-your-plate--wizard');
        navigation.hidden = false;
        this.querySelector('[data-running-total]').hidden = false;
        this.stylesPanel = this.sectionRoot.querySelector('.build-your-plate__styles');
        this.updateWizard();
      }

      activeSteps() {
        const extras = this.querySelector('[data-group="extras"]');
        const hasExtras = (extras && !extras.hidden) || (this.keyringField && !this.keyringField.hidden);
        return hasExtras ? [0, 1, 2, 3, 4] : [0, 1, 2, 4];
      }

      advanceStep() {
        if (!this.stepPanels || this.nextButton.disabled || this._adding) return;
        const steps = this.activeSteps();
        this.goToStep(steps[steps.indexOf(this.currentStep) + 1]);
      }

      goToStep(step, focus = true) {
        if (!this.stepPanels || this._adding || !this.activeSteps().includes(step)) return;
        if (step > this.currentStep) {
          if (!this.currentVariant || this.currentVariant.available === false) return;
          if (step > 1 && ((this.requireReg && !this.regEntered()) || !this.validateRegistration())) {
            step = 1;
          }
        }
        this.currentStep = step;
        this.highestStep = Math.max(this.highestStep, step);
        this.clearError();
        this.updateWizard();
        if (focus) {
          const target = step === 1 ? this.regInput : this.stepPanels[step].querySelector('h3');
          target.focus({ preventScroll: true });
          if (step === 1 && window.matchMedia('(max-width: 989px)').matches) {
            this.keepMobileFieldVisible();
          } else if (window.matchMedia('(max-width: 989px)').matches) {
            this.scrollToMobileStep();
          } else {
            this.querySelector('[data-step-navigation]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }

      updateWizard(validReg = this.validateRegistration()) {
        if (!this.stepPanels) return;
        const steps = this.activeSteps();
        if (!steps.includes(this.currentStep)) {
          this.currentStep = 4;
          this.highestStep = Math.max(this.highestStep, 4);
        }
        this.stepPanels.forEach((panel, index) => { panel.hidden = index !== this.currentStep; });
        this.stepButtons.forEach((button, index) => {
          button.parentElement.hidden = !steps.includes(index);
          button.disabled = this._adding || index > this.highestStep;
          button.querySelector('[data-step-number]').textContent = String(steps.indexOf(index) + 1);
          if (index === this.currentStep) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
          button.classList.toggle('is-complete', index < this.currentStep);
        });
        this.backButton.hidden = this.currentStep === 0;
        this.backButton.disabled = !!this._adding;
        this.nextButton.hidden = this.currentStep === 4;
        const variantBlocked = !this.currentVariant || this.currentVariant.available === false;
        const regBlocked = !validReg || (this.requireReg && !this.regEntered());
        this.nextButton.disabled = !!this._adding || variantBlocked || (this.currentStep === 1 && regBlocked);
        const next = steps[steps.indexOf(this.currentStep) + 1];
        this.nextButton.textContent = next === 4 ? 'Review your plates' : 'Continue';
        this.stepStatus.textContent = `Step ${steps.indexOf(this.currentStep) + 1} of ${steps.length}`;
        const total = this.querySelector('[data-wizard-total]');
        if (total && this.totalEl) total.textContent = this.totalEl.textContent;
        if (this.reviewRegistration) this.reviewRegistration.textContent = `Registration: ${this.regInput.value.trim() || 'Not entered'}`;
        if (this.stylesPanel) this.stylesPanel.hidden = this.currentStep !== 2;
      }

      mergeRelatedProducts() {
        if (!this.data.relatedProducts) return;
        const names = this.data.options.map((name) => name.toLowerCase());
        const styleIdx = names.indexOf(this.dataset.styleOption);
        const positionIdx = names.indexOf(this.dataset.positionOption);
        const sizeIdx = names.indexOf(this.dataset.sizeOption);
        const vehicleIdx = names.indexOf(this.dataset.vehicleOption);
        const sizeName = (size) => ({
          'Standard Car': 'Standard', 'Standard oblong': 'Standard',
          'Hex Short 5': 'Hex 5', 'Hex Short 6': 'Hex 6', 'Hex Short 7': 'Hex 7',
          'Oversize': 'Oversize rear', 'Oversized': 'Oversize rear',
        }[size] || size);
        const allowedSizes = this.sizeDefinitions().map(([name]) => name);
        const supportedSize = size => allowedSizes.includes(size) || allowedSizes.some(front =>
          !this.rearOnlySize(front) && allowedSizes.some(rear => size === `${front} front + ${rear}`)
        );
        const variants = this.data.variants
          .filter((variant) => vehicleIdx < 0 || !/motorcycle/i.test(variant.options[vehicleIdx]))
          .map((variant) => ({ ...variant, options: [variant.options[styleIdx], variant.options[positionIdx], sizeName(sizeIdx < 0 ? 'Standard' : variant.options[sizeIdx])] }))
          .filter((variant) => supportedSize(variant.options[2]));
        for (const product of this.data.relatedProducts) {
          for (const variant of product.variants) {
            let style, position, size;
            if (product.handle === 'standard-size-printed-plates' && variant.options[1] === 'Standard print') {
              style = 'Printed'; position = variant.options[0]; size = 'Standard';
            } else if (product.handle === '4d-laser-cut-retro-bevel-legal-plates') {
              style = '4D Retro Bevel'; position = variant.options[0]; size = sizeName(variant.options[1]);
            } else if (['mixed-size-number-plate-pairs', 'number-plate-size-options', 'mixed-size-hex-plate-pairs'].includes(product.handle)) {
              [style, position, size] = variant.options;
            } else if (product.handle === '4d-motorcycle-number-plate') {
              if (variant.title === '533 x 152mm Printed') style = 'Printed';
              if (variant.title === '533 x 152mm 4D 5mm Black letters') style = '4D 5mm';
              position = 'Rear'; size = 'Oversize rear';
              if (/^Large Rear 4x4 Trailer 11x8 /i.test(variant.title)) {
                size = 'Square rear (11 x 8)';
                if (/Printed$/i.test(variant.title)) style = 'Printed';
                else if (/3D GEL$/i.test(variant.title)) style = '3D Gel';
                else if (/4D 5mm Black Letters$/i.test(variant.title)) style = '4D 5mm';
              }
            }
            if (position === 'Pair') position = 'Full Set';
            if (!style || !supportedSize(size) || !['Front', 'Rear', 'Full Set'].includes(position)) continue;
            const options = [style, position, size];
            if (!variants.some((existing) => existing.options.every((value, index) => value === options[index]))) {
              variants.push({ ...variant, options });
            }
          }
        }
        this.data.options = [this.dataset.styleOption, this.dataset.positionOption, this.dataset.sizeOption];
        this.data.variants = variants;
      }

      sizeDefinitions() {
        return [
          ['Standard', 520, 111], ['Short 5', 340, 111], ['Short 6', 406, 111],
          ['Short 7', 457, 111], ['Hex 5', 402, 111], ['Hex 6', 467, 111],
          ['Hex 7', 519, 111], ['Square rear (11 x 8)', 279.4, 203.2], ['Oversize rear', 533, 152],
        ];
      }

      populateSizes() {
        const select = this.querySelector('select[data-option="size"]');
        if (!select || !this.data.relatedProducts) return;
        const previous = select.value;
        select.replaceChildren();
        this.sizeDefinitions().forEach(([value]) => {
          const exists = this.data.variants.some((variant) => variant.options[this.sizeIdx] === value);
          const option = document.createElement('option');
          option.value = value;
          const label = value === 'Standard' ? 'Standard UK car'
            : value === 'Square rear (11 x 8)' ? 'Large square rear (11 × 8 inches)'
            : /^(Short|Hex) [567]$/.test(value) ? value.replace(/^(Short|Hex) ([567])$/, '$1 plate — $2 characters')
            : value;
          option.textContent = `${label}${exists ? '' : ' — Enquire to order'}`;
          option.disabled = !exists;
          select.appendChild(option);
        });
        if (Array.from(select.options).some((option) => option.value === previous && !option.disabled)) select.value = previous;
        const enquiry = this.querySelector('[data-size-enquiry]');
        if (enquiry) enquiry.hidden = true;
        const rear = this.querySelector('select[data-option="rear-size"]');
        if (rear) rear.replaceChildren(...Array.from(select.options, option => option.cloneNode(true)));
      }

      rearOnlySize(size) {
        return /oversiz|^Square rear/i.test(size || '');
      }

      rearSize() {
        return this.independentSizes() ? this.selected('rear-size') : this.selected('size');
      }

      independentSizes(position = this.selected('position')) {
        return this.isFullSet(position) && !this.motorcycleSelected() && !!this.differentSizesInput?.checked;
      }

      frontSize(position = this.selected('position')) {
        const size = this.selected('size');
        return this.isFullSet(position) && !this.independentSizes(position) && this.rearOnlySize(size) ? 'Standard' : size;
      }

      variantSize(position = this.selected('position')) {
        const front = this.frontSize(position);
        const rear = this.rearSize();
        return this.isFullSet(position) && front !== rear ? `${front} front + ${rear}` : front;
      }

      bindEvents() {
        if (this.regInput) {
          this.regInput.addEventListener('input', () => {
            // Preserve the registration when changing size; never truncate it.
            const clean = this.regInput.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/ +/g, ' ');
            if (clean !== this.regInput.value) this.regInput.value = clean;
            this.updatePreview();
            this.updateProps();
            this.refreshSubmit();
          });
        }

        this.addEventListener('change', (e) => {
          if (e.target === this.differentSizesInput) {
            const front = this.querySelector('[data-option="size"]');
            const rear = this.querySelector('[data-option="rear-size"]');
            if (e.target.checked) {
              rear.value = front.value;
              if (this.rearOnlySize(front.value)) front.value = 'Standard';
            } else if (front.value === 'Standard' && this.rearOnlySize(rear.value)) {
              front.value = rear.value;
            }
          }
          if (e.target.matches('input[type="radio"], select[data-option], [data-addon], [data-border], [data-different-sizes]')) this.update();
        });

        // Validate every submission, including Enter, before Dawn handles it.
        if (this.form) {
          this.form.addEventListener('submit', this.handleAddonSubmit.bind(this), true);
        }

        this.documentUploads.forEach((input) => {
          input.addEventListener('change', () => {
            this.clearError();
            this.refreshSubmit();
          });
        });

        // Clicking a style card selects that style in the builder.
        this.sectionRoot.querySelectorAll('[data-select-style], [data-select-bike-variant]').forEach((card) => {
          card.addEventListener('click', (e) => {
            if (e.target.closest('a')) return; // let real links work

            const bikeVariantId = card.getAttribute('data-select-bike-variant');
            if (bikeVariantId) {
              const safeBikeId = bikeVariantId.replace(/["\\]/g, '\\$&');
              const bikeInput = this.querySelector(`input[data-bike-variant][value="${safeBikeId}"]`);
              if (bikeInput && !bikeInput.disabled) {
                bikeInput.checked = true;
                this.update();
                this.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              return;
            }

            const value = card.getAttribute('data-select-style');
            if (!value) return;
            // Quoted attribute selector: only " and \ need escaping (NOT CSS.escape).
            const safe = value.replace(/["\\]/g, '\\$&');
            const input = this.querySelector(`input[data-option="style"][value="${safe}"]`);
            if (input && !input.disabled) {
              input.checked = true;
              this.update();
              this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        });
      }

      selected(name) {
        const select = this.querySelector(`select[data-option="${name}"]`);
        if (select) return select.value;
        const el = this.querySelector(`input[data-option="${name}"]:checked, input[data-${name}]:checked`);
        return el ? el.value : null;
      }

      addonSelected() {
        return !!(this.addonInput && this.addonInput.checked && !this.addonInput.disabled && this.addonId);
      }

      borderSelected() {
        return !!(this.borderInput && this.borderInput.checked && !this.borderInput.disabled && this.borderId);
      }

      badgesEligible() {
        if (this.motorcycleSelected() || !/^printed$/i.test(this.selected('style') || '')) return false;
        const sizes = this.isFullSet(this.selected('position')) ? [this.frontSize(), this.rearSize()] : [this.selected('size')];
        return sizes.every(size => size === 'Standard' || size === 'Oversize rear');
      }

      badgeVariant(badge = this.selected('badge')) {
        if (!this.badgesEligible() || !badge || badge === 'None') return null;
        const position = this.isFullSet(this.selected('position')) ? 'Pair' : 'Single';
        return this.badgeVariants.find(variant => variant.badge === badge && variant.position === position && variant.available) || null;
      }

      updateBadges() {
        const eligible = this.badgesEligible();
        this.badgeInputs.forEach(input => {
          const variant = this.badgeVariant(input.value);
          input.disabled = input.value !== 'None' && !variant;
          const price = this.querySelector('[data-badge-price="' + input.value + '"]');
          if (price && input.value !== 'None') price.textContent = variant ? '+' + this.formatMoney(variant.price) : 'Unavailable';
        });
        if (!this.badgeVariant()) {
          const none = this.badgeInputs.find(input => input.value === 'None');
          if (none) none.checked = true;
        }
        if (this.badgeField) this.badgeField.hidden = this.motorcycleSelected();
        const help = this.querySelector('[data-badge-help]');
        if (help) help.textContent = eligible
          ? 'Printed badges: £3 per pair or £2 per single plate.'
          : 'Badges are available for standard and oversize plates with Printed lettering. Both plates in a pair must use an eligible size.';
        const badge = this.badgeVariant();
        const line = this.querySelector('[data-line-badge]');
        if (line) line.hidden = !badge;
        if (badge) {
          this.querySelector('[data-badge-summary]').textContent = (badge.badge === 'EV' ? 'Electric vehicle' : badge.badge) + ' printed badge · ' + badge.position.toLowerCase();
          this.querySelector('[data-badge-total]').textContent = '+' + this.formatMoney(badge.price);
        }
        this.querySelectorAll('[data-preview-badge]').forEach(el => { el.toggleAttribute('hidden', !badge || el.dataset.previewBadge !== badge.badge); });
      }

      registrationLimit() {
        if (this.motorcycleSelected()) return 7;
        const sizes = [this.selected('size')];
        if (this.isFullSet(this.selected('position'))) sizes.push(this.rearSize());
        return Math.min(...sizes.map(size => {
          const capacity = /(?:short|hex)[\s_-]*(?:short[\s_-]*)?([567])(?:\D|$)/i.exec(size || '');
          return capacity ? Number(capacity[1]) : 7;
        }));
      }

      validateRegistration() {
        const limit = this.registrationLimit();
        const value = this.regInput ? this.regInput.value : '';
        const count = value.replace(/[^A-Z0-9]/gi, '').length;
        const invalid = count > limit;
        const message = invalid ? `Your registration has ${count} characters. This size allows up to ${limit}. Choose a larger plate size or check your registration.` : '';
        if (this.regInput) {
          this.regInput.setCustomValidity(message);
          this.regInput.setAttribute('aria-invalid', String(invalid));
        }
        if (this.regError) {
          this.regError.textContent = message;
          this.regError.hidden = !invalid;
        }
        return !invalid;
      }

      isFullSet(positionVal) {
        return (positionVal || '').toLowerCase().indexOf('full') > -1;
      }

      deliverySelected() {
        if (this.motorcycleSelected()) return false;
        const fulfilment = (this.selected('fulfilment') || '').toLowerCase();
        return this.deliveryCents > 0 && fulfilment.indexOf('delivery') > -1;
      }

      motorcycleSelected() {
        return (this.selected('vehicle') || '').toLowerCase().indexOf('motorcycle') > -1;
      }

      selectedBikeVariant() {
        const input = this.querySelector('[data-bike-variant]:checked');
        if (!input) return null;

        return {
          id: input.value,
          title: input.dataset.bikeTitle || 'Bike Plates',
          available: input.dataset.bikeAvailable !== 'false',
          price: parseInt(input.dataset.bikePrice, 10) || 0,
          priceFormatted: input.dataset.bikePriceFormatted || this.formatMoney(parseInt(input.dataset.bikePrice, 10) || 0),
        };
      }

      bikeFinish(title) {
        if (/retro|bevel/i.test(title)) return '4D Retro Bevel';
        if (/4d/i.test(title)) return /5\s*mm/i.test(title) ? '4D 5mm' : '4D 3mm';
        return /gel/i.test(title) ? '3D Gel' : 'Printed';
      }

      updateBikeOptions(motorcycleOn) {
        const size = this.selected('bike-size') || '9x7';
        const previous = this.selectedBikeVariant();
        const finish = previous ? this.bikeFinish(previous.title) : 'Printed';
        const matching = this.bikeVariantInputs.filter(input => input.dataset.bikeSize === size);
        const available = matching.filter(input => input.dataset.bikeAvailable !== 'false');
        const selected = available.find(input => this.bikeFinish(input.dataset.bikeTitle) === finish) || available[0] || matching[0];
        this.bikeVariantInputs.forEach(input => {
          input.disabled = !motorcycleOn || input.dataset.bikeSize !== size || input.dataset.bikeAvailable === 'false';
          input.closest('label').hidden = input.dataset.bikeSize !== size;
          input.closest('label').querySelector('[data-bike-finish-label]').textContent = this.bikeFinish(input.dataset.bikeTitle);
          input.checked = input === selected;
        });
        const select = this.querySelector('[data-option="bike-size"]');
        if (select) {
          select.disabled = !motorcycleOn;
          Array.from(select.options).forEach(option => {
            option.disabled = !this.bikeVariantInputs.some(input => input.dataset.bikeSize === option.value && input.dataset.bikeAvailable !== 'false');
          });
        }
      }

      updateModeVisibility(motorcycleOn) {
        const sizeField = this.querySelector('[data-group="size"]');
        if (sizeField) sizeField.hidden = motorcycleOn;
        const bikeSizeField = this.querySelector('[data-group="bike-size"]');
        if (bikeSizeField) bikeSizeField.hidden = !motorcycleOn;
        const sizeSelect = this.querySelector('select[data-option="size"]');
        if (sizeSelect) sizeSelect.disabled = motorcycleOn;
        if (this.differentSizesInput) this.differentSizesInput.disabled = motorcycleOn;
        const rearSelect = this.querySelector('[data-option="rear-size"]');
        if (rearSelect) rearSelect.disabled = motorcycleOn || !this.independentSizes();
        if (this.positionField) this.positionField.hidden = motorcycleOn;
        if (this.styleField) this.styleField.hidden = motorcycleOn;
        if (this.bikeStyleField) this.bikeStyleField.hidden = !motorcycleOn;
        if (this.addonField) this.addonField.hidden = motorcycleOn;
        if (this.fulfilmentField) this.fulfilmentField.hidden = motorcycleOn;

        this.querySelectorAll('[data-option="position"], [data-option="style"]').forEach((input) => {
          input.disabled = motorcycleOn;
        });
        this.updateBikeOptions(motorcycleOn);
        this.fulfilmentInputs.forEach((input) => {
          input.disabled = motorcycleOn;
        });
        if (this.addonInput) {
          this.addonInput.disabled = motorcycleOn || this.addonInitiallyDisabled;
        }
        const extras = this.querySelector('[data-group="extras"]');
        if (extras) extras.hidden = !this.borderInput && (!this.badgeField || motorcycleOn) && (!this.addonInput || motorcycleOn);

        this.legalStyleCards.forEach((card) => {
          card.hidden = motorcycleOn;
        });
        this.bikeStyleCards.forEach((card) => {
          const input = this.bikeVariantInputs.find(input => input.value === card.dataset.selectBikeVariant);
          card.hidden = !motorcycleOn || !input || input.dataset.bikeSize !== this.selected('bike-size');
        });
      }

      updatePreview() {
        const val = this.regInput ? this.regInput.value.trim() : '';
        [this.previewPlate, this.previewRear].filter(Boolean).forEach((plate, index) => {
          let text = val || this.placeholder;
          const [, width, height] = this.previewDimensions(index === 1);
          if (height > width / 2) {
            const compact = text.replace(/ /g, '');
            const split = text.includes(' ') ? text.indexOf(' ') : Math.max(1, compact.length - 3);
            text = `${compact.slice(0, split)}\n${compact.slice(split)}`;
          }
          plate.querySelectorAll('[data-plate-preview-text], [data-preview-depth]').forEach(el => {
            el.textContent = text;
          });
        });
        this.updatePreviewGeometry();
        this.updatePreviewFinish();
      }

      updatePreviewFinish() {
        if (!this.stack) return;
        const bikeVariant = this.motorcycleSelected() ? this.selectedBikeVariant() : null;
        const style = bikeVariant ? bikeVariant.title : this.selected('style') || '';
        let finish = ['printed', 'Printed', 'Flat black lettering'];
        if (/retro|bevel/i.test(style)) finish = ['retro', '4D Retro Bevel', 'Raised lettering with bevelled edges'];
        else if (/4d/i.test(style) && /5\s*mm/i.test(style)) finish = ['4d-5mm', '4D 5mm', 'Deeper raised acrylic lettering'];
        else if (/4d/i.test(style)) finish = ['4d-3mm', '4D 3mm', 'Crisp raised acrylic lettering'];
        else if (/gel/i.test(style)) finish = ['gel', '3D Gel', 'Glossy, rounded lettering'];
        else if (!/print/i.test(style)) finish = ['printed', 'Lettering preview', 'Flat black lettering'];
        this.stack.dataset.previewFinish = finish[0];
        const caption = this.querySelector('[data-preview-finish-caption]');
        if (caption) caption.textContent = `${finish[1]} · ${finish[2]}`;
      }

      updatePreviewGeometry() {
        if (!this.stack) return;
        const captions = [];
        [this.previewPlate, this.previewRear].filter(Boolean).forEach((plate, index) => {
          const [name, width, height] = this.previewDimensions(index === 1);
          const hex = /^Hex/.test(name);
          const outline = (inset, left = inset) => {
            const right = width - inset;
            const bottom = height - inset;
            if (hex) {
              const cut = 26 + inset;
              return `M ${cut} ${inset} H ${width - cut} L ${right} ${height / 2} L ${width - cut} ${bottom} H ${cut} L ${inset} ${height / 2} Z`;
            }
            const radius = 4;
            return `M ${left + radius} ${inset} H ${right - radius} Q ${right} ${inset} ${right} ${inset + radius} V ${bottom - radius} Q ${right} ${bottom} ${right - radius} ${bottom} H ${left + radius} Q ${left} ${bottom} ${left} ${bottom - radius} V ${inset + radius} Q ${left} ${inset} ${left + radius} ${inset} Z`;
          };
          // All sizes use the same scale, so short plates visibly shrink.
          const scale = this.stack.clientWidth / 533;
          const sample = plate.querySelector('[data-plate-preview-text]').textContent;
          let font = 115 * scale;
          this.previewMeasure = this.previewMeasure || document.createElement('canvas').getContext('2d');
          this.previewMeasure.font = `700 ${font}px "Plate Preview", "Arial Narrow", Arial`;
          const textWidth = Math.max(1, ...sample.split('\n').map(line =>
            this.previewMeasure.measureText(line).width + line.length * 0.035 * font + 0.06 * font
          ));
          // Flag badges sit inset; the EV strip follows the left face edge.
          const badgeWidth = 40;
          const badgeInset = this.badgeVariant()?.badge === 'EV' ? 1 : 11;
          // Reserve equal margins before a badge is chosen, keeping the registration
          // centred and the same size when any badge is selected or removed.
          const textMargin = this.badgeField && this.badgesEligible() ? 11 + badgeWidth + 11 : 20;
          font = Math.min(font, font * ((width - 2 * textMargin) * scale) / textWidth);
          // Keep single-line lettering consistent on standard and oversize plates.
          // Two-line lettering also needs enough vertical space inside the border.
          const lines = sample.split('\n').length;
          const textHeight = lines === 1 ? Math.min(height, 111) : height;
          font = Math.min(font, (textHeight - 22) * scale / (lines * 0.95));
          plate.style.setProperty('--plate-preview-width', `${width / 533 * 100}%`);
          plate.style.setProperty('--plate-preview-ratio', `${width} / ${height}`);
          plate.style.setProperty('--plate-preview-font', `${font}px`);
          plate.style.setProperty('--plate-badge-width', `${badgeWidth / width * 100}%`);
          plate.style.setProperty('--plate-badge-left', `${badgeInset / width * 100}%`);
          plate.dataset.previewSize = name;
          plate.querySelector('[data-preview-shape]').setAttribute('viewBox', `0 0 ${width} ${height}`);
          plate.querySelector('[data-preview-face]').setAttribute('d', outline(1));
          plate.querySelector('[data-preview-badge="EV"]')?.setAttribute('d', `M 5 1 H ${1 + badgeWidth} V ${height - 1} H 5 Q 1 ${height - 1} 1 ${height - 5} V 5 Q 1 1 5 1 Z`);
          // The EV border frames the reflective area, leaving the green strip outside it.
          const borderLeft = this.badgeVariant()?.badge === 'EV' ? badgeWidth + 7 : 7;
          plate.querySelector('[data-preview-border]').setAttribute('d', outline(7, borderLeft));
          if (!plate.hidden) {
            const label = this.motorcycleSelected() ? '' : index === 1 || /^rear$/i.test(this.selected('position')) ? 'Rear: ' : 'Front: ';
            captions.push(`${label}${name} — ${width} × ${height} mm`);
          }
        });
        this.stack.dataset.previewSize = this.previewDimensions()[0];
        const caption = this.querySelector('[data-preview-dimensions]');
        if (caption) caption.textContent = captions.join('\n');
        const bikeDetails = this.querySelector('[data-bike-size-details]');
        if (bikeDetails && this.motorcycleSelected()) {
          const [, width, height] = this.previewDimensions();
          bikeDetails.textContent = `${width} × ${height} mm. ${this.selected('bike-size') === '9x7' ? 'Choose your lettering finish in the next step.' : 'Printed lettering only — £15 per plate.'}`;
        }
      }

      previewDimensions(rear = false) {
        if (this.motorcycleSelected()) {
          return ({'9x7': ['Motorcycle 9 × 7', 228.6, 177.8], '8x6': ['Motorcycle 8 × 6', 203.2, 152.4], '7x5': ['Motorcycle 7 × 5', 177.8, 127]})[this.selected('bike-size')] || ['Motorcycle 9 × 7', 228.6, 177.8];
        }
        const size = this.isFullSet(this.selected('position')) ? (rear ? this.rearSize() : this.frontSize()) : this.selected('size');
        return this.sizeDefinitions().find(([name]) => name === size) || this.sizeDefinitions()[0];
      }

      // Show a white front and yellow rear together when a full set is selected.
      updatePreviewLayout(positionVal) {
        const pos = (positionVal || '').toLowerCase();
        if (this.previewPlate) {
          const fullSet = this.isFullSet(positionVal);
          this.previewPlate.classList.toggle('plate-preview--front', pos.indexOf('front') > -1 || fullSet);
          if (this.previewRear) this.previewRear.hidden = !fullSet;
          if (this.stack) this.stack.classList.toggle('is-set', fullSet);
          return;
        }

        let showFront = false;
        let showRear = true;
        if (pos.indexOf('full') > -1) showFront = true;
        else if (pos.indexOf('front') > -1) {
          showFront = true;
          showRear = false;
        }
        if (this.previewFront) this.previewFront.hidden = !showFront;
        if (this.previewRear) this.previewRear.hidden = !showRear;
        if (this.stack) this.stack.classList.toggle('is-set', showFront && showRear);
      }

      // Find the variant whose mapped option values all equal the current
      // selection. Unmapped options are ignored.
      findVariant(styleVal, positionVal, vehicleVal) {
        if (this.rearOnlySize(this.selected('size')) && /^front$/i.test(positionVal || '')) return undefined;
        return (this.data.variants || []).find((v) => {
          if (this.styleIdx > -1 && v.options[this.styleIdx] !== styleVal) return false;
          if (this.positionIdx > -1 && v.options[this.positionIdx] !== positionVal) return false;
          if (this.vehicleIdx > -1 && v.options[this.vehicleIdx] !== vehicleVal) return false;
          if (this.sizeIdx > -1 && v.options[this.sizeIdx] !== this.variantSize(positionVal)) return false;
          return true;
        });
      }

      updatePositionAvailability() {
        const frontOnly = /^front$/i.test(this.selected('position'));
        const fullSet = this.isFullSet(this.selected('position'));
        const independent = this.independentSizes();
        const select = this.querySelector('select[data-option="size"]');
        Array.from(select.options).forEach(option => {
          const exists = this.data.variants.some(variant => variant.options[this.sizeIdx] === option.value);
          option.disabled = !exists || ((frontOnly || independent) && this.rearOnlySize(option.value));
        });
        if ((frontOnly || independent) && this.rearOnlySize(select.value)) select.value = 'Standard';
        select.setAttribute('aria-label', independent ? 'Front plate size' : 'Plate size');
        this.querySelector('[data-different-sizes-control]').hidden = !fullSet;
        this.differentSizesInput.disabled = !fullSet;
        this.differentSizesInput.setAttribute('aria-expanded', String(independent));
        this.querySelector('[data-front-size-label]').hidden = !independent;
        this.querySelector('[data-rear-size-field]').hidden = !independent;
        this.querySelector('[data-option="rear-size"]').disabled = !independent;
        const explanation = this.querySelector('[data-size-explanation]');
        if (explanation) {
          explanation.hidden = !fullSet;
          explanation.textContent = independent ? 'Choose a size for each plate. Your lettering finish will apply to both.' : this.rearOnlySize(select.value)
            ? `Includes a standard front plate and ${select.value === 'Oversize rear' ? 'an oversize rear' : 'an 11 × 8 square rear'}. Adds £5 to the pair price.`
            : 'Your chosen size applies to both front and rear plates.';
        }
        const pricing = this.querySelector('[data-size-pricing]');
        if (pricing) {
          pricing.hidden = !independent && this.rearOnlySize(select.value);
          pricing.textContent = independent ? 'Both standard: normal pair price. One non-standard: +£5. Both non-standard: +£10.' : 'Standard sizes are included. Short and hex sizes add £5 per plate.';
        }
      }

      update() {
        let styleVal = this.selected('style');
        const vehicleVal = this.selected('vehicle');
        const motorcycleOn = this.motorcycleSelected();

        this.updateModeVisibility(motorcycleOn);
        if (!motorcycleOn) this.updatePositionAvailability();
        const positionVal = this.selected('position');
        this.updatePreviewLayout(motorcycleOn ? 'Rear' : positionVal);
        const borderOn = this.borderSelected();
        if (this.previewPlate) this.previewPlate.classList.toggle('plate-preview--border', borderOn);
        if (this.previewRear) this.previewRear.classList.toggle('plate-preview--border', borderOn);
        if (this.borderLine) this.borderLine.hidden = !borderOn;
        this.updateKeyringVisibility(positionVal);

        let variant = this.findVariant(styleVal, positionVal, vehicleVal);
        if (!motorcycleOn && (!variant || !variant.available)) {
          const styles = Array.from(this.querySelectorAll('input[data-option="style"]'));
          const availableStyle = styles.find((input) => {
            const match = this.findVariant(input.value, positionVal, vehicleVal);
            return match && match.available;
          }) || styles.find((input) => this.findVariant(input.value, positionVal, vehicleVal));
          if (availableStyle) {
            availableStyle.checked = true;
            styleVal = availableStyle.value;
            variant = this.findVariant(styleVal, positionVal, vehicleVal);
          }
        }

        this.updateBadges();

        // A size change can select a different available finish. Render after
        // resolving that choice so the preview matches the purchasable variant.
        this.updatePreview();

        if (motorcycleOn) {
          const bikeVariant = this.selectedBikeVariant();
          this.currentVariant = bikeVariant || null;
          if (this.variantIdInput) this.variantIdInput.value = bikeVariant ? bikeVariant.id : '';
          if (this.summaryEl) this.summaryEl.textContent = bikeVariant ? bikeVariant.title : 'Bike Plates';
          if (this.platePriceEl) this.platePriceEl.textContent = bikeVariant ? bikeVariant.priceFormatted : '-';
          if (this.totalEl) this.totalEl.textContent = bikeVariant ? this.formatMoney(bikeVariant.price + (borderOn ? this.borderCents : 0)) : '-';
          if (this.addonLine && !this.addonLineIsControl) this.addonLine.hidden = true;
          if (this.deliveryLine) this.deliveryLine.hidden = true;
          this.querySelectorAll('[data-style-price]').forEach((el) => {
            el.textContent = '';
          });
          this.updateProps();
          this.refreshSubmit();
          return;
        }

        this.currentVariant = variant || null;

        const availability = this.querySelector('[data-size-availability]');
        if (availability) {
          const matches = this.data.variants.filter((item) => item.options[this.sizeIdx] === this.variantSize(positionVal) && item.options[this.positionIdx] === positionVal);
          const finishes = [...new Set(matches.filter((item) => item.available).map((item) => item.options[this.styleIdx]))];
          availability.textContent = finishes.length ? `Available finishes: ${finishes.join(', ')}.` : matches.length ? 'This size and plate position is currently sold out.' : this.independentSizes() ? 'These sizes are not available together in the same lettering finish. Choose another front or rear size.' : 'This combination is not available online. Contact us to order.';
        }

        // A finish is purchasable only when this exact size/position has stock.
        this.querySelectorAll('input[data-option="style"]').forEach((input) => {
          const match = this.findVariant(input.value, positionVal, vehicleVal);
          input.disabled = !match || !match.available;
        });

        // Update the per-style price hints for the current position/vehicle.
        this.querySelectorAll('[data-style-price]').forEach((el) => {
          const v = this.findVariant(el.getAttribute('data-style-price'), positionVal, vehicleVal);
          el.textContent = v ? v.priceFormatted : '';
        });

        // Summary line — what they're configuring, e.g. "4D 5mm · Full Set".
        if (this.summaryEl) {
          const sizes = this.isFullSet(positionVal) ? `Front: ${this.frontSize()} · Rear: ${this.rearSize()}` : this.selected('size');
          const parts = [styleVal, this.isFullSet(positionVal) ? 'Front & Rear' : positionVal, sizes].filter(Boolean);
          this.summaryEl.textContent = parts.length ? parts.join(' · ') : 'Your plate';
        }

        // Fitting-kit breakdown line.
        const addonOn = this.addonSelected();
        const deliveryOn = this.deliverySelected();
        if (this.addonLine && !this.addonLineIsControl) this.addonLine.hidden = !addonOn;
        if (this.addonLineIsControl || addonOn) {
          if (!this.addonLineIsControl && this.addonSummary && this.addonTitle) this.addonSummary.textContent = this.addonTitle;
          if (this.addonPriceEl) {
            this.addonPriceEl.textContent = '+' + (this.addonPriceFormatted || this.formatMoney(this.addonCents));
          }
        }
        if (this.deliveryLine) this.deliveryLine.hidden = !deliveryOn;
        if (this.deliveryPriceEl) {
          this.deliveryPriceEl.textContent = '+' + (this.deliveryPriceFormatted || this.formatMoney(this.deliveryCents));
        }

        if (variant) {
          this.variantIdInput.value = variant.id;
          if (this.platePriceEl) this.platePriceEl.textContent = variant.priceFormatted;
          const badge = this.badgeVariant();
          const totalCents = (badge ? badge.price : 0) + variant.price + (addonOn ? this.addonCents : 0) + (borderOn ? this.borderCents : 0) + (deliveryOn ? this.deliveryCents : 0);
          if (this.totalEl) {
            this.totalEl.textContent = badge || addonOn || borderOn || deliveryOn ? this.formatMoney(totalCents) : variant.priceFormatted;
          }
        } else {
          // No matching variant — block add-to-cart rather than charge wrong price.
          if (this.variantIdInput) this.variantIdInput.value = '';
          if (this.platePriceEl) this.platePriceEl.textContent = '—';
          if (this.totalEl) this.totalEl.textContent = '—';
        }

        this.updateProps();
        this.refreshSubmit();
      }

      updateKeyringVisibility(positionVal) {
        const showKeyring = !this.motorcycleSelected() && this.isFullSet(positionVal);
        if (this.keyringField) this.keyringField.hidden = !showKeyring;
        this.keyringInputs.forEach((input) => {
          input.disabled = !showKeyring;
        });
        if (this.propKeyring) this.propKeyring.disabled = !showKeyring;
        if (this.keyringReview) {
          const selected = this.querySelector('[data-keyring]:checked');
          this.keyringReview.hidden = !showKeyring || !selected;
          this.keyringReviewColour.textContent = selected?.value || '';
          const imageUrl = selected?.dataset.keyringImage;
          this.keyringReviewImage.hidden = !imageUrl;
          if (imageUrl && this.keyringReviewImage.getAttribute('src') !== imageUrl) {
            this.keyringReviewImage.src = imageUrl;
          }
          this.keyringReviewImage.alt = selected ? selected.value + ' keyring' : '';
        }
      }

      regEntered() {
        return this.regInput ? /[A-Z0-9]/i.test(this.regInput.value) : true;
      }

      // Decide whether checkout is allowed and what the button should say.
      // Precedence: no variant → sold out → missing reg → ready.
      refreshSubmit() {
        const validReg = this.validateRegistration();
        this.updateWizard(validReg);
        if (this._adding) return this.setSubmit(false, 'Adding...');
        if (!this.currentVariant) return this.setSubmit(false, 'Unavailable');
        if (this.currentVariant.available === false) return this.setSubmit(false, 'Sold out');
        if (this.requireReg && !this.regEntered()) return this.setSubmit(false, 'Enter your reg first');
        if (!validReg) return this.setSubmit(false, 'Check registration length');
        return this.setSubmit(true, null);
      }

      setSubmit(enabled, label) {
        if (!this.submitBtn) return;
        this.submitBtn.disabled = !enabled;
        this.submitBtn.setAttribute('aria-disabled', String(!enabled));
        if (this.submitText) this.submitText.textContent = enabled ? this.defaultLabel : label || this.defaultLabel;
      }

      buildCartData() {
        const body = new FormData();
        const plateId = this.variantIdInput.value;
        body.append('items[0][id]', plateId);
        body.append('items[0][quantity]', '1');
        // FormData keeps document files intact, unlike JSON.stringify.
        for (const [name, value] of new FormData(this.form)) {
          if (!name.startsWith('properties[')) continue;
          if (value instanceof File && !value.size) continue;
          body.append(`items[0][${name.replace('[', '][')}`, value);
        }
        let index = 1;
        if (this.borderSelected() || this.badgeVariant()) {
          // Keep separate configurations of the same Shopify variant distinct.
          body.append('items[0][properties][_Plate configuration]', crypto.randomUUID());
        }
        if (this.borderSelected()) {
          body.append(`items[${index}][id]`, this.borderId);
          body.append(`items[${index}][quantity]`, '1');
          body.append(`items[${index}][parent_id]`, plateId);
          body.append(`items[${index}][properties][Registration]`, this.regInput.value.trim());
          body.append(`items[${index}][properties][Border]`, 'Black');
          body.append(`items[${index}][properties][Applies to]`, this.motorcycleSelected() ? 'Motorcycle' : this.selected('position'));
          body.append(`items[${index}][properties][_Plate border]`, 'true');
          index++;
        }
        const badge = this.badgeVariant();
        if (badge) {
          body.append('items[' + index + '][id]', badge.id);
          body.append('items[' + index + '][quantity]', '1');
          body.append('items[' + index + '][parent_id]', plateId);
          body.append('items[' + index + '][properties][Registration]', this.regInput.value.trim());
          body.append('items[' + index + '][properties][Badge]', badge.badge + ' (printed)');
          body.append('items[' + index + '][properties][Applies to]', this.selected('position'));
          body.append('items[' + index + '][properties][_Plate badge]', 'true');
          index++;
        }
        if (this.addonSelected()) {
          body.append(`items[${index}][id]`, this.addonId);
          body.append(`items[${index}][quantity]`, '1');
        }
        if (this.cart) {
          body.append('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
          body.append('sections_url', window.location.pathname);
        }
        return body;
      }

      async handleAddonSubmit(e) {
        this.updateProps();
        this.refreshSubmit();
        if (this.stepPanels && this.currentStep !== 4) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.advanceStep();
          return;
        }
        if (this.submitBtn.disabled || this._adding) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        if (!this.addonSelected() && !this.borderSelected() && !this.badgeVariant()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.clearError();
        this._adding = true;
        this.refreshSubmit();
        try {
          const body = this.buildCartData();
          if (this.cart) this.cart.setActiveElement(document.activeElement);
          const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/(?:\.js)?$/, '.js');
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add your plates and extras.');
          const plate = data.items && data.items.find((item) => !item.parent_relationship && String(item.id) === body.get('items[0][id]'));
          if (!this.cart || !plate || !data.sections || Object.values(data.sections).some((section) => !section)) {
            window.location.assign((window.routes && window.routes.cart_url) || '/cart');
            return;
          }
          try {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents({ ...data, key: plate.key, id: plate.id });
          } catch (_) {
            // Items are already added; avoid a retry that could duplicate them.
            window.location.assign((window.routes && window.routes.cart_url) || '/cart');
          }
        } catch (err) {
          this.showError(`${err.message || 'Could not confirm the basket update.'} Please check your basket before trying again.`);
        } finally {
          this._adding = false;
          this.refreshSubmit();
        }
      }

      showError(msg) {
        if (this.errorMessage) this.errorMessage.textContent = msg;
        if (this.errorWrapper) this.errorWrapper.hidden = false;
      }

      clearError() {
        if (this.errorMessage) this.errorMessage.textContent = '';
        if (this.errorWrapper) this.errorWrapper.hidden = true;
      }

      // Minimal Shopify money formatter (handles the {{amount}} placeholders).
      formatMoney(cents) {
        const value = (cents / 100).toFixed(2);
        const parts = value.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
        return this.moneyFormat.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, (m, noDec) =>
          noDec ? dollars : dollars + '.' + parts[1]
        );
      }

      updateProps() {
        const motorcycleOn = this.motorcycleSelected();
        const bikeVariant = motorcycleOn ? this.selectedBikeVariant() : null;
        const position = this.selected('position');
        this.toggleProp(this.propFrontSize, !motorcycleOn && (this.isFullSet(position) || /^front$/i.test(position)), this.frontSize());
        this.toggleProp(this.propRearSize, !motorcycleOn && (this.isFullSet(position) || /^rear$/i.test(position)), this.isFullSet(position) ? this.rearSize() : this.selected('size'));

        if (this.propReg) this.propReg.value = this.regInput ? this.regInput.value.trim() : '';
        if (this.propKeyring) {
          const includeKeyring = !motorcycleOn && this.isFullSet(this.selected('position'));
          this.propKeyring.disabled = !includeKeyring;
          this.propKeyring.value = includeKeyring ? this.selected('keyring') || '' : '';
        }
        if (this.propFulfilment) {
          this.propFulfilment.disabled = motorcycleOn;
          this.propFulfilment.value = motorcycleOn ? '' : this.selected('fulfilment') || '';
        }

        if (motorcycleOn) {
          this.toggleProp(this.propStyle, true, bikeVariant ? bikeVariant.title : 'Bike Plates');
          this.toggleProp(this.propPosition, false, '');
          this.toggleProp(this.propVehicle, true, this.selected('vehicle'));
          return;
        }

        // Only send the fallback property when the choice ISN'T a variant option
        // (otherwise it's already recorded in the variant title — avoid duplicates).
        this.toggleProp(this.propStyle, this.styleIdx === -1, this.selected('style'));
        this.toggleProp(this.propPosition, this.positionIdx === -1, this.selected('position'));
        this.toggleProp(this.propVehicle, this.vehicleIdx === -1, this.selected('vehicle'));
      }

      toggleProp(input, active, value) {
        if (!input) return;
        input.disabled = !active; // disabled inputs aren't submitted
        input.value = active ? value || '' : '';
      }
    }
  );
}
